import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import cpApi from "@/lib/clientPortalApi";
import { PageHeader, fmtDate, StatusPill } from "./_cpShared";
import { Loader2, FileQuestion, ArrowRight } from "lucide-react";

export default function CpRfis() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cpApi.get("/client-portal/rfis").then((r) => setRows(r.data || [])).finally(() => setLoading(false));
  }, []);

  const open = rows.filter((r) => r.status !== "Closed");
  const closed = rows.filter((r) => r.status === "Closed");

  return (
    <div>
      <PageHeader
        subtitle="Requests for information from Griffin"
        title="Queries / RFIs"
      />
      <div className="p-4 sm:p-8 space-y-6">
        <Section title={`Open & In Progress (${open.length})`} rows={open} loading={loading} emptyHint="No open queries — you're all caught up." />
        {closed.length > 0 && (
          <Section title={`Closed (${closed.length})`} rows={closed} loading={false} emptyHint="" />
        )}
      </div>
    </div>
  );
}

function Section({ title, rows, loading, emptyHint }) {
  return (
    <div>
      <h3 className="text-xs tracking-[0.18em] uppercase text-slate-500 mb-2">{title}</h3>
      <div className="bg-white border border-slate-200 rounded-sm">
        {loading ? (
          <div className="p-12 flex justify-center"><Loader2 className="animate-spin" /></div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400 flex flex-col items-center gap-2">
            <FileQuestion size={28} className="text-slate-300" />
            {emptyHint}
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {rows.map((r) => (
              <li key={r.id} data-testid={`cp-rfi-row-${r.id}`}>
                <Link to={`/client-portal/queries/${r.id}`} className="block p-4 hover:bg-slate-50 group">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[9px] tracking-[0.18em] uppercase font-semibold px-1.5 py-0.5 rounded-sm ${prioCls(r.priority)}`}>{r.priority || "Medium"}</span>
                        <StatusPill status={r.status} />
                        {r.response_count > 0 && (
                          <span className="text-[10px] text-slate-500">· {r.response_count} response{r.response_count === 1 ? "" : "s"}</span>
                        )}
                      </div>
                      <div className="text-sm font-semibold tracking-tight truncate">{r.title}</div>
                      {r.description && (
                        <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{r.description}</div>
                      )}
                      <div className="text-[11px] text-slate-400 mt-1.5">
                        Asked by {r.created_by_staff_name || "Griffin"} · {fmtDate(r.created_at)}
                        {r.due_date && <span> · Due {fmtDate(r.due_date)}</span>}
                      </div>
                    </div>
                    <ArrowRight size={16} className="text-slate-300 group-hover:text-[#0a2540] mt-1" />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function prioCls(p) {
  if (p === "High") return "bg-red-100 text-red-800";
  if (p === "Low") return "bg-slate-100 text-slate-700";
  return "bg-amber-100 text-amber-800";
}
